import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import tenant from '../../config/tenant';
import { getAuthKeyFromLocalStorage } from './employeeSlice';
import holidayService from '../../services/holidayService';

async function fetchWithAuth(url, token) {
  console.debug(`[filterAttendanceFetch] GET ${url}`);
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['auth-key'] = token;

  const res = await fetch(url, { method: 'GET', headers });
  console.debug(`[filterAttendanceFetch] Response status: ${res.status}`);

  if (res.status === 404) return { __noData: true };
  if (!res.ok) {
    const text = await res.text().catch(() => `HTTP ${res.status}`);
    throw new Error(text || `HTTP error ${res.status}`);
  }
  const json = await res.json();
  return json;
}

// ── OT-hours / summary endpoint ────────────────────────────────────────────
export const fetchFilterAttendanceDetails = createAsyncThunk(
  'filterAttendance/fetchDetails',
  async ({ year, month }, { rejectWithValue }) => {
    const token = getAuthKeyFromLocalStorage();
    const url = `${tenant.api.baseUrl}/Attendence/GetFILLTERAttendenceDetails?year=${encodeURIComponent(year)}&month=${encodeURIComponent(month)}`;

    try {
      const data = await fetchWithAuth(url, token);
      if (data && data.__noData) return [];
      if (data && data.StatusCode !== undefined && data.StatusCode !== 200) {
        if (data.StatusCode === 404 || /no\s*(data|record)/i.test(data.Message || '')) return [];
        throw new Error(data.Message || `Server error: status ${data.StatusCode}`);
      }
      if (Array.isArray(data)) return data;
      const resultSet = data ? (data.ResultSet ?? data.Result ?? data.Data) : null;
      return Array.isArray(resultSet) ? resultSet : (resultSet ? [resultSet] : []);
    } catch (err) {
      console.error('[filterAttendance] fetchDetails error:', err);
      return rejectWithValue(err.message || 'Network error. Check your connection.');
    }
  }
);

// ── Per-day attendance endpoint ────────────────────────────────────────────
export const fetchFilterAttendanceDaily = createAsyncThunk(
  'filterAttendance/fetchDaily',
  async ({ year, month }, { rejectWithValue }) => {
    const token = getAuthKeyFromLocalStorage();
    const url = `${tenant.api.baseUrl}/Attendence/GetFILLTERAttDetails?year=${encodeURIComponent(year)}&month=${encodeURIComponent(month)}`;

    try {
      const data = await fetchWithAuth(url, token);
      if (data && data.__noData) return [];
      if (data && data.StatusCode !== undefined && data.StatusCode !== 200) {
        if (data.StatusCode === 404 || /no\s*(data|record)/i.test(data.Message || '')) return [];
        throw new Error(data.Message || `Server error: status ${data.StatusCode}`);
      }
      if (Array.isArray(data)) return data;
      const resultSet = data ? (data.ResultSet ?? data.Result ?? data.Data) : null;
      return Array.isArray(resultSet) ? resultSet : (resultSet ? [resultSet] : []);
    } catch (err) {
      console.error('[filterAttendance] fetchDaily error:', err);
      return rejectWithValue(err.message || 'Network error. Check your connection.');
    }
  }
);

// ── Helpers ────────────────────────────────────────────────────────────────
const toYMD = (d) => {
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return null;
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// ── Monthly attendance summary ─────────────────────────────────────────────
export const fetchMonthlyAttendanceSummary = createAsyncThunk(
  'filterAttendance/fetchMonthlySummary',
  async ({ year, month }, { rejectWithValue }) => {
    const token = getAuthKeyFromLocalStorage();

    try {
      // 1. Fetch daily attendance
      const attUrl = `${tenant.api.baseUrl}/Attendence/GetFILLTERAttDetails?year=${encodeURIComponent(year)}&month=${encodeURIComponent(month)}`;
      const attRaw = await fetchWithAuth(attUrl, token);

      let attRows = [];
      if (Array.isArray(attRaw)) attRows = attRaw;
      else if (attRaw?.ResultSet) attRows = attRaw.ResultSet;
      else if (attRaw?.Result) attRows = attRaw.Result;
      else if (attRaw?.Data) attRows = attRaw.Data;

      // 2. Fetch holidays
      const holRaw = await holidayService.getAllHolidays(token);
      const holRows = holRaw?.ResultSet || [];

      // Build holiday date sets (keyed by "YYYY-MM-DD")
      const publicHolidays = new Set();
      const mercantileHolidays = new Set();
      const poyaHolidays = new Set();

      holRows.forEach((h) => {
        if (h?.HAH_IS_ACTIVE === 'False') return;
        const ymd = toYMD(h.HAH_HOLIDAY_DATE);
        if (!ymd) return;

        const type = String(h.HAH_HOLIDAY_TYPE || '').toLowerCase();
        const name = String(h.HAH_HOLIDAY_NAME || '').toLowerCase();

        if (type === 'public') {
          publicHolidays.add(ymd);
          if (name.includes('poya')) poyaHolidays.add(ymd);
        } else if (type === 'mercantile') {
          mercantileHolidays.add(ymd);
        }
      });

      // 3. Group per employee, de-dup per day (one attendance per day max)
      const byEmp = new Map();

      attRows.forEach((row) => {
        const serviceNo = String(row.ServiceNo || '').trim();
        if (!serviceNo) return;

        const ymd = toYMD(row.AttDate);
        if (!ymd) return;

        const dt = new Date(row.AttDate);
        const isSunday = dt.getDay() === 0;
        const isPoya = poyaHolidays.has(ymd);
        const isPublicHoliday = publicHolidays.has(ymd);
        const isMercantile = mercantileHolidays.has(ymd);
        const isHoliday = isPoya || isPublicHoliday || isMercantile;

        if (!byEmp.has(serviceNo)) byEmp.set(serviceNo, new Map());
        const inner = byEmp.get(serviceNo);
        if (!inner.has(ymd)) {
          inner.set(ymd, {
            ymd,
            isSunday,
            isPoya,
            isPublicHoliday,
            isMercantile,
            isHoliday,
          });
        }
      });

      // 4. Reduce to summary numbers
      const summary = {};

      byEmp.forEach((dayMap, serviceNo) => {
        let workingDays = 0;
        let offDays = 0;
        let holidaysWorked = 0;
        let poyaDaysWorked = 0;

        dayMap.forEach((info) => {
          if (info.isSunday || info.isHoliday) {
            offDays += 1;
            if (info.isPublicHoliday || info.isMercantile) holidaysWorked += 1;
            if (info.isPoya) poyaDaysWorked += 1;
          } else {
            workingDays += 1;
          }
        });

        summary[serviceNo] = {
          workingDays,
          offDays,
          attendanceDays: dayMap.size,
          holidaysWorked,
          poyaDaysWorked,
        };
      });

      return summary;
    } catch (err) {
      console.error('[filterAttendance] fetchMonthlyAttendanceSummary error:', err);
      return rejectWithValue(err.message || 'Network error. Check your connection.');
    }
  }
);

export const parseYearMonth = (ym) => {
  if (!ym) return null;
  const [y, m] = ym.split('-');
  if (!y || !m) return null;
  return { year: y, month: m };
};

const initialState = {
  rows: [],
  dailyRows: [],
  monthlySummary: {},
  loading: false,
  summaryLoading: false,
  error: null,
};

const filterAttendanceSlice = createSlice({
  name: 'filterAttendance',
  initialState,
  reducers: {
    clearFilterAttendanceError(state) { state.error = null; },
    resetFilterAttendance(state) {
      state.rows = [];
      state.dailyRows = [];
      state.monthlySummary = {};
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Summary (OT hours)
      .addCase(fetchFilterAttendanceDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFilterAttendanceDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.rows = action.payload;
      })
      .addCase(fetchFilterAttendanceDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Failed to load attendance details';
        state.rows = [];
      })

      // Daily (per-day attendance)
      .addCase(fetchFilterAttendanceDaily.fulfilled, (state, action) => {
        state.dailyRows = action.payload;
      })
      .addCase(fetchFilterAttendanceDaily.rejected, (state, action) => {
        console.warn('[filterAttendance] daily fetch failed:', action.payload);
        state.dailyRows = [];
      })

      // Monthly summary
      .addCase(fetchMonthlyAttendanceSummary.pending, (state) => {
        state.summaryLoading = true;
        state.error = null;
      })
      .addCase(fetchMonthlyAttendanceSummary.fulfilled, (state, action) => {
        state.summaryLoading = false;
        state.monthlySummary = action.payload || {};
      })
      .addCase(fetchMonthlyAttendanceSummary.rejected, (state, action) => {
        state.summaryLoading = false;
        state.error = action.payload ?? 'Failed to load attendance summary';
      });
  },
});

export const selectMonthlySummary = (state) =>
  state.filterAttendance?.monthlySummary || {};
export const { clearFilterAttendanceError, resetFilterAttendance } = filterAttendanceSlice.actions;
export default filterAttendanceSlice.reducer;